import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const api = axios.create({ baseURL: API, withCredentials: true });
// Multi-area: X-Area = area yang dipilih master admin (kosong = ikut subdomain/area utama); X-Area-Host = hostname browser untuk deteksi subdomain
export const getActiveArea = () => localStorage.getItem("active_area") || "";
api.interceptors.request.use((config) => {
  const a = getActiveArea();
  if (a) config.headers["X-Area"] = a;
  config.headers["X-Area-Host"] = window.location.hostname;
  return config;
});
// Sesi berakhir/token tidak valid: kembali ke halaman login (cookie httpOnly dibersihkan server)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || "";
    if (status === 401 && !url.includes("/auth/login") && !url.includes("/auth/me")) {
      window.location.href = "/";
    }
    return Promise.reject(err);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=auth
  const [activeArea, setActiveAreaState] = useState(getActiveArea());
  const setActiveArea = useCallback((kode) => {
    if (kode) localStorage.setItem("active_area", kode); else localStorage.removeItem("active_area");
    setActiveAreaState(kode || "");
  }, []);

  useEffect(() => {
    // Sesi tersimpan di cookie httpOnly — cukup tanyakan ke server
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  const login = useCallback(async (nomorPegawai, password, remember = true, areaKode = "") => {
    const { data } = await api.post("/auth/login",
      { nomor_pegawai: nomorPegawai, password, remember }, { headers: areaKode ? { "X-Area": areaKode } : {} });
    if (remember) localStorage.setItem("remember_nomor", nomorPegawai);
    else localStorage.removeItem("remember_nomor");
    // Master: mulai dari halaman Master (tanpa area aktif). Pengguna area: kunci area pilihannya agar tiap request membawa X-Area.
    const kode = data.user?.is_master ? "" : (areaKode || "");
    if (kode) localStorage.setItem("active_area", kode); else localStorage.removeItem("active_area");
    setActiveAreaState(kode);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // sesi server mungkin sudah kedaluwarsa; sesi lokal tetap dibersihkan
    }
    localStorage.removeItem("active_area");
    setActiveAreaState("");
    setUser(false);
  }, []);

  // useMemo mencegah object baru tiap render (menghindari re-render konsumen context)
  const value = useMemo(() => ({ user, login, logout, activeArea, setActiveArea }), [user, login, logout, activeArea, setActiveArea]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// ---- Akses per modul (cermin dari backend core.py) ----
export const SEMUA_MODUL = ["beranda", "pekerja", "area", "keuangan", "barang", "ba", "riwayat", "helpdesk", "asset", "pm", "lembur", "petty_cash"];
const MODUL_DEFAULT = {
  admin: SEMUA_MODUL,
  staf: ["beranda", "pekerja", "area", "keuangan", "ba", "riwayat"],
  staf_hk: ["barang", "lembur"],
  staf_ba: ["ba"],
  staf_pekerja: ["pekerja"],
  approved_eng: ["pm"],
  approved_hk: ["pm"],
  petugas_eng: ["pm"],
  petugas_hk: ["pm"],
};
export const userModules = (user) => {
  const role = user?.role || "admin";
  if (role === "admin") return SEMUA_MODUL;
  let m = (user?.modules || []).filter((x) => SEMUA_MODUL.includes(x));
  if (!m.length) m = MODUL_DEFAULT[role] || [];
  const ar = user?.approval_roles || [];
  if ((ar.includes("reviewer") || ar.includes("approver")) && !m.includes("lembur")) m = [...m, "lembur"];
  return m;
};
